import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { diaryService } from 'services'
import { calculateNutritionDetails, emptyArray, hasEmpty } from 'utils/helpers'
import { selectDiaryFilter } from './filters.slice'
import { selectFood } from './food.slice'

const diaryDataAdapter = createEntityAdapter()

const initialState = {
  data: diaryDataAdapter.getInitialState(),
  status: 'idle',
  error: null,
}

export const fetchData = createAsyncThunk('diary/fetch', async date => diaryService.getItems(date))

export const addDataItem = createAsyncThunk('diary/add', async (item, api) => {
  const added = await diaryService.addItem(item)
  return [added, selectDiaryFilter(api.getState()).isToday]
})

export const updateDataItem = createAsyncThunk('diary/update', async x => diaryService.updateItem(x))

export const deleteDataItem = createAsyncThunk('diary/delete', async id => diaryService.deleteItem(id))

const slice = createSlice({
  name: 'diary',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchData.fulfilled, (state, action) => {
        state.status = 'succeeded'
        diaryDataAdapter.removeAll(state.data)
        diaryDataAdapter.upsertMany(state.data, action.payload)
      })
      .addCase(addDataItem.fulfilled, (state, action) => {
        state.status = 'succeeded'
        const [item, updateState] = action.payload
        if (updateState) diaryDataAdapter.upsertOne(state.data, item)
      })
      .addCase(updateDataItem.fulfilled, (state, action) => {
        state.status = 'succeeded'
        diaryDataAdapter.upsertOne(state.data, action.payload)
      })
      .addCase(deleteDataItem.fulfilled, (state, action) => {
        state.status = 'succeeded'
        diaryDataAdapter.removeOne(state.data, action.payload)
      })
      .addMatcher(
        isAnyOf(fetchData.pending, addDataItem.pending, updateDataItem.pending, deleteDataItem.pending),
        state => {
          state.status = 'loading'
        },
      )
      .addMatcher(
        isAnyOf(fetchData.rejected, addDataItem.rejected, updateDataItem.rejected, deleteDataItem.rejected),
        (state, action) => {
          state.status = 'failed'
          state.error = action.error
        },
      )
  },
})

export default slice.reducer

export const selectDataStatus = state => state.diary.status

export const selectDataError = state => state.diary.error

export const { selectAll: selectAllDayData, selectById: selectDayDataById } = diaryDataAdapter.getSelectors(
  state => state.diary.data,
)

export const selectDayData = state => state.diary.data.entities

export const selectDayInfo = createSelector([selectFood, selectAllDayData], (food, data) => {
  if (hasEmpty(food, data)) return emptyArray

  return data.map(x => {
    const foodItem = food[x.foodItemId]

    const { calories, protein, fat, carbohydrate } = calculateNutritionDetails(foodItem, x.weight)

    return { ...x, name: foodItem?.name, calories, protein, fat, carbohydrate, foodItem }
  })
})
